/**
 * ZoneRenderer — the shrinking wall, drawn as a storm edge in the pass-2
 * language: hazard tape and ink, not a glow.
 *
 * The zone closes to RADIUS ZERO and it decides the last thirty seconds of a
 * wagered match, so "clearly readable" is a requirement and not a polish item.
 * Three cues, deliberately redundant:
 *
 *   1. The WALL — a cylinder you can see from anywhere in the arena, drawn
 *      from the inside. A TAPE band hugs the floor (diagonal stripes of the
 *      zone colour and ink under an ink line), and above it a short, stepped
 *      curtain of rising bars thins out fast, so at eye level it is a
 *      boundary and never a veil.
 *   2. The GROUND RING — where the wall meets the floor: an opaque, ink-rimmed
 *      band of the same tape, a constant 1.4 units wide however big the
 *      circle is. The only cue that survives when the wall is behind you.
 *   3. The TARGET RING — a thin dashed circle at the radius the zone is
 *      contracting toward, visible during the 5 s warning. Knowing where the
 *      next circle lands is the difference between rotating and being caught.
 *
 * ALPHA-BLENDED, not additive. The old additive wall added ~half its colour
 * to every sky pixel behind it and read as a powder-blue veil (and vanished
 * over a bright deck). Paint with an ink edge reads on any ground and stays
 * inside a fixed opacity budget. Also cheaper: the wall is 24 units tall now,
 * not 70 — the top two thirds of the old one were rasterised at alpha 0.
 *
 * All three read the server's zone. The client never derives a radius from the
 * clock; it draws the number it was sent.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, uv, time, sin, smoothstep, mix, float, vec3, uniform, positionWorld, positionLocal,
  fract, step, atan, length, oneMinus, abs, max,
} from 'three/tsl';

export const WALL_HEIGHT = 24;
const WALL_SEGMENTS = 96;
/** Below this the cylinder degenerates; the closing flare covers the last metre. */
const MIN_DRAW_RADIUS = 0.6;
/** World width of the ground band and of the target ring's dashes. */
export const RING_WIDTH = 1.4;
export const TARGET_WIDTH = 0.34;
/** Tape stripe pitch along the ring, in world units. */
const STRIPE_PITCH = 1.7;

/** --ult cyan when the zone is merely a boundary; hazard red once it burns. */
const CALM = new THREE.Color(0x22d3ee);
const LETHAL = new THREE.Color(0xff3d3d);
const INK = new THREE.Color(0x04080a);

export class ZoneRenderer {
  constructor(scene) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.group.name = 'zone';
    scene.add(this.group);

    // Live-ness (0 calm → 1 damaging) scales the curtain's opacity and the
    // tape's brightness; danger picks the colour; finale pulses the tape.
    this._live = uniform(0.0);
    this._danger = uniform(0.0);
    this._finale = uniform(0.0);
    /** Stripes around the ring, so the tape pitch stays ~STRIPE_PITCH u at any radius. */
    this._stripes = uniform(60);
    this._targetStripes = uniform(60);
    /** Inner radius of each unit ring, as a fraction — see `_fitRing`. */
    this._ringInnerU = uniform(0.9);
    this._targetInnerU = uniform(0.97);

    this.wall = new THREE.Mesh(
      // Unit radius, scaled per frame — resizing a CylinderGeometry every frame
      // would rebuild a 96-segment buffer 60 times a second.
      new THREE.CylinderGeometry(1, 1, WALL_HEIGHT, WALL_SEGMENTS, 1, true),
      this._wallMaterial()
    );
    this.wall.renderOrder = 5;
    this.group.add(this.wall);

    // The ring is rebuilt (rarely, with hysteresis) so its band stays a
    // constant world width: a proportional ring is fat at r45 and a hairline
    // at r5, which is exactly when it matters most.
    this._ringInner = 0;
    this.groundRing = new THREE.Mesh(new THREE.RingGeometry(0.9, 1, WALL_SEGMENTS), this._ringMaterial(false));
    this.groundRing.rotation.x = -Math.PI / 2;
    this.groundRing.renderOrder = 6;
    this.group.add(this.groundRing);

    this._targetInner = 0;
    this.targetRing = new THREE.Mesh(new THREE.RingGeometry(0.97, 1, WALL_SEGMENTS), this._ringMaterial(true));
    this.targetRing.rotation.x = -Math.PI / 2;
    this.targetRing.renderOrder = 6;
    this.targetRing.visible = false;
    this.group.add(this.targetRing);

    this._floorY = 0.02;
  }

  _zoneColor() {
    const pulse = sin(time.mul(4.0)).mul(0.5).add(0.5);
    // A hard turn from cyan to red once it burns: the linear mix the old wall
    // used passed through grey at the first contraction's dps 6.
    const base = mix(vec3(CALM.r, CALM.g, CALM.b), vec3(LETHAL.r, LETHAL.g, LETHAL.b), smoothstep(float(0.0), float(0.3), this._danger));
    // The finale flashes the tape toward white on the beat rather than
    // brightening additively.
    return mix(base, vec3(1.0), this._finale.mul(pulse).mul(0.55));
  }

  _wallMaterial() {
    const material = new THREE.MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    // Seen from inside the cylinder, which is where every player is.
    material.side = THREE.BackSide;
    material.blending = THREE.NormalBlending;

    const live = this._live;
    const finale = this._finale;
    const ink = vec3(INK.r, INK.g, INK.b);

    const v = uv().y; // 0 at the floor, 1 at the top
    // Diagonal tape: angle × stripes plus height, scrolling slowly.
    const tape = step(0.5, fract(uv().x.mul(72.0).add(positionWorld.y.mul(0.9)).sub(time.mul(0.35))));
    // The band hugging the floor: bottom 1.7 of 24 units.
    const band = oneMinus(smoothstep(float(0.062), float(0.074), v));
    const inkTop = smoothstep(float(0.068), float(0.076), v).mul(oneMinus(smoothstep(float(0.086), float(0.094), v)));
    // The curtain: three stepped levels thinning upward, bars rising through it.
    const rise = step(0.55, fract(v.mul(7.0).sub(time.mul(0.7))));
    const lvl = step(0.0, v).sub(step(0.22, v).mul(0.45)).sub(step(0.40, v).mul(0.35)).sub(step(0.58, v));
    const curtain = max(lvl, 0.0).mul(rise.mul(0.5).add(0.5)).mul(oneMinus(band)).mul(oneMinus(inkTop));

    material.colorNode = Fn(() => {
      const zone = this._zoneColor();
      let color = mix(zone, ink, tape.mul(band));
      color = mix(color, ink, inkTop);
      return color;
    })();

    material.opacityNode = Fn(() => {
      const pulse = sin(time.mul(4.0)).mul(0.5).add(0.5);
      // Calm: a faint marker; live: a band you cannot miss. The curtain's
      // eye-level contribution is capped by its own levels (≤ 0.16) so the
      // far wall never veils the fight behind it.
      const bandA = band.mul(float(0.32).add(live.mul(0.5)));
      const curtainA = curtain.mul(float(0.06).add(live.mul(0.10))).mul(float(1.0).add(finale.mul(pulse).mul(0.6)));
      return bandA.add(inkTop.mul(0.9)).add(curtainA);
    })();

    // A wall of paint must not be fogged: it is the edge of the world, and
    // fog on it made the far side read as weather.
    material.fog = false;
    return material;
  }

  _ringMaterial(dashed) {
    const material = new THREE.MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    material.side = THREE.DoubleSide;
    material.blending = THREE.NormalBlending;

    const ink = vec3(INK.r, INK.g, INK.b);
    const live = this._live;
    const stripes = dashed ? this._targetStripes : this._stripes;
    const inner = dashed ? this._targetInnerU : this._ringInnerU;

    // Polar coordinates off the unit ring: `uv()` on a RingGeometry is planar
    // and useless for stripes.
    const ang = atan(positionLocal.y, positionLocal.x).div(Math.PI * 2);
    const rad = length(positionLocal.xy);
    // 0 at the inner edge, 1 at the outer, whatever the inner radius is.
    const across = rad.sub(inner).div(oneMinus(inner));
    const aa = float(0.06);
    const rimA = oneMinus(smoothstep(float(0.0), aa.mul(2.2), across));
    const rimB = smoothstep(oneMinus(aa.mul(2.2)), float(1.0), across);
    const rim = max(rimA, rimB);

    material.colorNode = Fn(() => {
      const zone = this._zoneColor();
      if (dashed) return mix(zone, ink, rim.mul(0.85));
      const tape = step(0.5, fract(ang.mul(stripes).add(across.mul(0.5)).add(time.mul(0.25))));
      let color = mix(zone, ink, tape.mul(0.9));
      color = mix(color, ink, rim);
      return color;
    })();

    material.opacityNode = Fn(() => {
      if (dashed) {
        // Rotating dashes mark the ring you are being asked to move to, so
        // it is never confused with the wall you are currently inside.
        const dash = step(0.5, fract(ang.mul(stripes).mul(0.5).add(time.mul(0.3))));
        return dash.mul(0.85);
      }
      return float(0.62).add(live.mul(0.34));
    })();

    material.fog = false;
    return material;
  }

  setFloorY(y) {
    // 0.045 slots the rings between the arena deck (floor + 0.02) and ability
    // telegraph decals (floor + 0.06): under the deck they vanish, above the
    // telegraphs they would paint over the thing you dodge by.
    this._floorY = y + 0.045;
  }

  /**
   * Rebuilds a unit ring when its band has drifted more than 20% from the
   * wanted world width: a rebuild every fifth of the radius, ~17 over a
   * match from r45 to the last metre, each a 192-vertex buffer.
   */
  _fitRing(mesh, radius, width, currentInner, innerUniform) {
    const inner = Math.max(0.05, Math.min(0.995, (radius - width) / radius));
    if (currentInner > 0) {
      const have = (1 - currentInner) * radius;
      if (Math.abs(have - width) <= width * 0.2 || Math.abs(inner - currentInner) < 1e-6) return currentInner;
    }
    mesh.geometry.dispose();
    mesh.geometry = new THREE.RingGeometry(inner, 1, WALL_SEGMENTS);
    innerUniform.value = inner;
    return inner;
  }

  /**
   * @param {{centerX:number,centerZ:number,radius:number,targetRadius:number,dps:number}} zone
   * @param {boolean} warning true during the 5s pre-contraction window
   */
  update(zone, warning) {
    const r = Math.max(zone.radius, MIN_DRAW_RADIUS);

    this.wall.visible = zone.radius > MIN_DRAW_RADIUS;
    this.wall.position.set(zone.centerX, this._floorY + WALL_HEIGHT / 2, zone.centerZ);
    this.wall.scale.set(r, 1, r);

    this._ringInner = this._fitRing(this.groundRing, r, RING_WIDTH, this._ringInner, this._ringInnerU);
    this._stripes.value = Math.max(8, Math.round((2 * Math.PI * r) / STRIPE_PITCH));
    this.groundRing.position.set(zone.centerX, this._floorY, zone.centerZ);
    this.groundRing.scale.set(r, r, 1);

    const showTarget = warning && zone.targetRadius > MIN_DRAW_RADIUS && zone.targetRadius < zone.radius;
    this.targetRing.visible = showTarget;
    if (showTarget) {
      const tr = zone.targetRadius;
      this._targetInner = this._fitRing(this.targetRing, tr, TARGET_WIDTH, this._targetInner, this._targetInnerU);
      this._targetStripes.value = Math.max(8, Math.round((2 * Math.PI * tr) / STRIPE_PITCH));
      this.targetRing.position.set(zone.centerX, this._floorY, zone.centerZ);
      this.targetRing.scale.set(tr, tr, 1);
    }

    // dps runs 6 → 15 over the match. Normalising against the end value makes
    // the wall visibly angrier as it gets deadlier.
    this._danger.value = Math.min(1, zone.dps / 15);
    this._live.value = zone.dps > 0 ? 1 : 0;
    // Final phase: the contraction is heading to radius zero (targetRadius is 0
    // exactly from ZONE_FINAL_START_TICK on) while damage is live. The gym's
    // zone-off placeholder reports dps 0, so it never pulses.
    this._finale.value = zone.dps > 0 && zone.targetRadius <= MIN_DRAW_RADIUS ? 1 : 0;
  }

  /** True when the player's XZ is outside the circle. Drives the HUD warning. */
  isOutside(zone, x, z) {
    const dx = x - zone.centerX;
    const dz = z - zone.centerZ;
    return Math.sqrt(dx * dx + dz * dz) > zone.radius;
  }
}
